import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);
    const [itemCount, setItemCount] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);

    // Carica il carrello da localStorage al avvio
    useEffect(() => {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
            try {
                const parsedCart = JSON.parse(savedCart);
                if (Array.isArray(parsedCart)) {
                    // Validazione e pulizia dei dati
                    const validatedCart = parsedCart.filter(item =>
                        item &&
                        item.id &&
                        typeof item.quantity === 'number' &&
                        item.quantity > 0 &&
                        typeof item.price === 'number' &&
                        item.price >= 0
                    );
                    setCart(validatedCart);
                    updateCartStats(validatedCart);
                }
            } catch (error) {
                console.error('Errore nel parsing del carrello:', error);
                localStorage.removeItem('cart');
                setCart([]);
            }
        }
        setIsInitialized(true);
    }, []);

    // Salva il carrello in localStorage ad ogni cambiamento
    useEffect(() => {
        if (isInitialized) {
            localStorage.setItem('cart', JSON.stringify(cart));
            updateCartStats(cart);
        }
    }, [cart, isInitialized]);

    // Aggiorna statistiche del carrello
    const updateCartStats = useCallback((cartItems) => {
        if (!Array.isArray(cartItems)) {
            setItemCount(0);
            return;
        }

        const count = cartItems.reduce((sum, item) => {
            return sum + (Number(item.quantity) || 0);
        }, 0);

        setItemCount(count);
    }, []);

    // Valida e normalizza prodotto
    const validateProduct = (product) => {
        if (!product || !product.id) {
            throw new Error('Prodotto non valido: ID mancante');
        }

        return {
            id: String(product.id),
            name: String(product.name || 'Prodotto senza nome'),
            price: Number(product.price) || 0,
            image: product.image || '',
            category: product.category || '',
            description: product.description || '',
            quantity: Math.max(1, Number(product.quantity) || 1)
        };
    };

    // Aggiungi un prodotto al carrello
    const addItem = useCallback((product, quantity = 1) => {
        try {
            const validatedProduct = validateProduct(product);
            const quantityNum = Math.max(1, Number(quantity) || 1);

            setCart(prevCart => {
                const existingItemIndex = prevCart.findIndex(item => item.id === validatedProduct.id);

                if (existingItemIndex >= 0) {
                    // Aggiorna quantità prodotto esistente
                    const updatedCart = [...prevCart];
                    updatedCart[existingItemIndex] = {
                        ...updatedCart[existingItemIndex],
                        quantity: updatedCart[existingItemIndex].quantity + quantityNum,
                        lastUpdated: Date.now()
                    };
                    return updatedCart;
                } else {
                    // Aggiungi nuovo prodotto
                    return [...prevCart, {
                        ...validatedProduct,
                        quantity: quantityNum,
                        addedAt: Date.now(),
                        lastUpdated: Date.now()
                    }];
                }
            });
        } catch (error) {
            console.error('Errore nell\'aggiunta al carrello:', error.message);
        }
    }, []);

    // Rimuovi un prodotto dal carrello
    const removeItem = useCallback((productId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
    }, []);

    // Aggiorna la quantità di un prodotto
    const updateQuantity = useCallback((productId, newQuantity) => {
        const quantityNum = Math.max(0, Math.floor(Number(newQuantity) || 0));

        if (quantityNum === 0) {
            removeItem(productId);
            return;
        }

        setCart(prevCart =>
            prevCart.map(item =>
                item.id === productId ? {
                    ...item,
                    quantity: quantityNum,
                    lastUpdated: Date.now()
                } : item
            )
        );
    }, [removeItem]);

    // Incrementa quantità prodotto
    const incrementQuantity = useCallback((productId, amount = 1) => {
        setCart(prevCart =>
            prevCart.map(item =>
                item.id === productId ? {
                    ...item,
                    quantity: Math.max(1, item.quantity + (Number(amount) || 1)),
                    lastUpdated: Date.now()
                } : item
            )
        );
    }, []);

    // Decrementa quantità prodotto
    const decrementQuantity = useCallback((productId, amount = 1) => {
        setCart(prevCart =>
            prevCart.map(item => {
                if (item.id === productId) {
                    const newQuantity = Math.max(0, item.quantity - (Number(amount) || 1));
                    if (newQuantity === 0) {
                        return null; // Filtriamo dopo
                    }
                    return {
                        ...item,
                        quantity: newQuantity,
                        lastUpdated: Date.now()
                    };
                }
                return item;
            }).filter(Boolean) // Rimuove null (prodotti con quantità 0)
        );
    }, []);

    // Svuota completamente il carrello
    const clearCart = useCallback(() => {
        setCart([]);
        localStorage.removeItem('cart');
    }, []);

    // Ottieni la quantità di un prodotto specifico
    const getItemQuantity = useCallback((productId) => {
        const item = cart.find(item => item.id === productId);
        return item ? Number(item.quantity) : 0;
    }, [cart]);

    // Calcola il totale del carrello
    const getCartTotal = useCallback(() => {
        return cart.reduce((sum, item) => {
            return sum + (Number(item.price) * Number(item.quantity));
        }, 0);
    }, [cart]);

    // Calcola il totale con la spedizione
    const getCartTotalWithShipping = useCallback((shippingCost = 5.00) => {
        const total = getCartTotal();
        return total + Number(shippingCost);
    }, [getCartTotal]);

    // Verifica se il carrello contiene un prodotto specifico
    const isInCart = useCallback((productId) => {
        return cart.some(item => item.id === productId);
    }, [cart]);

    // Ottieni il numero di articoli unici (non quantità totale)
    const getUniqueItemCount = useCallback(() => {
        return cart.length;
    }, [cart]);

    // Aggiorna un prodotto nel carrello
    const updateItem = useCallback((productId, updates) => {
        setCart(prevCart =>
            prevCart.map(item =>
                item.id === productId ? {
                    ...item,
                    ...updates,
                    lastUpdated: Date.now()
                } : item
            )
        );
    }, []);

    // Ottieni il subtotale per un prodotto specifico
    const getItemSubtotal = useCallback((productId) => {
        const item = cart.find(item => item.id === productId);
        if (!item) return 0;
        return Number(item.price) * Number(item.quantity);
    }, [cart]);

    // Verifica se il carrello è vuoto
    const isEmpty = useCallback(() => {
        return cart.length === 0;
    }, [cart]);

    // Calcola la quantità totale di prodotti (non articoli unici)
    const getTotalQuantity = useCallback(() => {
        return cart.reduce((total, item) => total + Number(item.quantity), 0);
    }, [cart]);

    // Ordina il carrello (per nome, prezzo, data aggiunta)
    const sortCart = useCallback((sortBy = 'addedAt', order = 'desc') => {
        setCart(prevCart => {
            const sorted = [...prevCart].sort((a, b) => {
                let aValue, bValue;

                switch (sortBy) {
                    case 'name':
                        aValue = a.name?.toLowerCase() || '';
                        bValue = b.name?.toLowerCase() || '';
                        return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                    case 'price':
                        aValue = Number(a.price) || 0;
                        bValue = Number(b.price) || 0;
                        return order === 'asc' ? aValue - bValue : bValue - aValue;
                    case 'addedAt':
                    default:
                        aValue = a.addedAt || 0;
                        bValue = b.addedAt || 0;
                        return order === 'asc' ? aValue - bValue : bValue - aValue;
                }
            });
            return sorted;
        });
    }, []);

    // Merge di due carrelli (utile per login/logout)
    const mergeCarts = useCallback((newCart) => {
        setCart(prevCart => {
            const mergedCart = [...prevCart];

            newCart.forEach(newItem => {
                const existingIndex = mergedCart.findIndex(item => item.id === newItem.id);

                if (existingIndex >= 0) {
                    // Somma le quantità se il prodotto esiste già
                    mergedCart[existingIndex] = {
                        ...mergedCart[existingIndex],
                        quantity: mergedCart[existingIndex].quantity + (newItem.quantity || 0),
                        lastUpdated: Date.now()
                    };
                } else {
                    // Aggiungi nuovo prodotto
                    mergedCart.push({
                        ...newItem,
                        addedAt: Date.now(),
                        lastUpdated: Date.now()
                    });
                }
            });

            return mergedCart;
        });
    }, []);

    // Valore del contesto
    const value = {
        cart,
        itemCount,
        isEmpty: isEmpty(),
        getCartTotal,
        getCartTotalWithShipping,
        getItemSubtotal,
        getItemQuantity,
        getTotalQuantity,
        getUniqueItemCount,
        isInCart,
        addItem,
        removeItem,
        updateQuantity,
        incrementQuantity,
        decrementQuantity,
        updateItem,
        clearCart,
        sortCart,
        mergeCarts
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};